// The planner timeline's single date <-> x mapping.
//
// Three regions, left to right:
//   left overflow zone  — fixed width, dates before linearStart, compressed
//   linear region       — linearStart..linearEnd, to scale (px per day)
//   right overflow zone — fixed width, dates after linearEnd, compressed
// linearStart is the first design milestone less TIMELINE_LEAD_MARGIN_DAYS and
// linearEnd the last one plus TIMELINE_TRAIL_MARGIN_DAYS. A zone is drawn only
// when some drawn date falls on its side. When nothing is drawn before the
// first milestone and the caller gives emptyLeadPx, the lead margin shrinks to
// that many pixels, so the timeline does not open on empty earlier dates.
// Likewise emptyTrailPx shrinks the trail margin when nothing is drawn after
// the last milestone.
//
// Inside a zone dates keep their order but are squeezed: the zone's span of
// days, from the furthest date to the linear edge, is spread over the zone
// width less an inner padding. So the furthest date sits OVERFLOW_ZONE_INNER_PAD_PX
// from the outer edge however far away it is, and the zone width never grows.
//
// Full timeline: no zones. The linear region keeps the same px per day and
// runs from the earliest drawn date to the latest (each with
// FULL_TIMELINE_EDGE_PAD_PX of room), never short of the default linear start
// and end.

/** Days drawn to scale before the first design milestone. */
export const TIMELINE_LEAD_MARGIN_DAYS = 60;
/** Days drawn to scale after the last design milestone. */
export const TIMELINE_TRAIL_MARGIN_DAYS = 60;
/** Width of each compressed overflow zone. */
export const OVERFLOW_ZONE_WIDTH_PX = 140;
/** Gap between a zone's outer edge and its furthest date. */
export const OVERFLOW_ZONE_INNER_PAD_PX = 8;
/** Scale used when the design milestones span no days (one milestone, or all
 *  on the same date): this many days fill MIN_SPAN_WIDTH_PX. */
const FALLBACK_SPAN_DAYS = 90;
const MIN_SPAN_WIDTH_PX = 900;
/** Room kept before the earliest and past the latest drawn date in
 *  full-timeline mode. */
export const FULL_TIMELINE_EDGE_PAD_PX = 24;

const MS_PER_DAY = 86_400_000;

/** UTC day number of an ISO date's calendar day (its first ten characters). */
export function dayOfIso(iso: string): number {
  return (
    Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) /
    MS_PER_DAY
  );
}

/** "YYYY-MM-DD" of a UTC day number. */
export function isoOfDay(day: number): string {
  const d = new Date(Math.round(day) * MS_PER_DAY);
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${d.getUTCFullYear()}-${m}-${dd}`;
}

export type OverflowZone = {
  side: "left" | "right";
  /** x of the zone's left and right edges. */
  start: number;
  end: number;
  /** The furthest date the zone holds. */
  furthestDay: number;
};

/** What an x means: a date on the linear region, or "before"/"after" the
 *  linear region when it is in (or past) a zone. */
export type ScalePoint =
  | { kind: "date"; day: number }
  | { kind: "before"; day: number }
  | { kind: "after"; day: number };

export type PlannerTimeScale = {
  firstMilestoneDay: number;
  lastMilestoneDay: number;
  linearStartDay: number;
  linearEndDay: number;
  pxPerDay: number;
  /** x of linearStartDay and linearEndDay. */
  linearStartX: number;
  linearEndX: number;
  left: OverflowZone | null;
  right: OverflowZone | null;
  /** Right edge of the drawn scale (the right zone's, when there is one). */
  width: number;
  /** date -> x, for a UTC day number. */
  x: (day: number) => number;
  /** x -> date. */
  at: (x: number) => ScalePoint;
  /** x -> fractional day, through the zones too (for keeping a date in view). */
  dayAt: (x: number) => number;
  /** Is the day inside a zone (not to scale)? */
  zoneOf: (day: number) => "left" | "right" | null;
};

export function buildPlannerTimeScale(input: {
  /** UTC day numbers of the dated design milestones. */
  milestoneDays: number[];
  /** px per day of the design span, as the milestone header draws it. Ignored
   *  when the milestones span no days. */
  pxPerDay?: number;
  /** Every other date the timeline draws (bid package card ends, Projected
   *  End, Deadline, Today). Only these decide whether a zone is drawn. */
  itemDays: number[];
  /** Lead margin, in px, used instead of TIMELINE_LEAD_MARGIN_DAYS when no
   *  item (of endItemDays, when given) falls before the first milestone.
   *  Never more than that margin. */
  emptyLeadPx?: number;
  /** Trail margin, in px, used instead of TIMELINE_TRAIL_MARGIN_DAYS when no
   *  item (of endItemDays, when given) falls after the last milestone. Never
   *  more than that margin. */
  emptyTrailPx?: number;
  /** Draw every item to scale: no zones, the linear region runs from the
   *  earliest item to the latest. */
  fullTimeline?: boolean;
  /** Full timeline: the dates whose earliest and latest set the left and right
   *  ends (the bid packages left by a filter). Defaults to itemDays. */
  endItemDays?: number[];
}): PlannerTimeScale | null {
  const days = input.milestoneDays.filter(Number.isFinite);
  if (!days.length) return null;
  const firstMilestoneDay = Math.min(...days);
  const lastMilestoneDay = Math.max(...days);
  const span = lastMilestoneDay - firstMilestoneDay;
  const pxPerDay =
    span > 0 && input.pxPerDay && input.pxPerDay > 0
      ? input.pxPerDay
      : MIN_SPAN_WIDTH_PX / FALLBACK_SPAN_DAYS;
  const items = input.itemDays.filter(Number.isFinite);
  const endItems = (input.endItemDays ?? items).filter(Number.isFinite);
  const nothingBefore = endItems.every((day) => day >= firstMilestoneDay);
  const leadDays =
    nothingBefore && input.emptyLeadPx != null
      ? Math.min(
          TIMELINE_LEAD_MARGIN_DAYS,
          Math.ceil(Math.max(0, input.emptyLeadPx) / pxPerDay)
        )
      : TIMELINE_LEAD_MARGIN_DAYS;
  const nothingAfter = endItems.every((day) => day <= lastMilestoneDay);
  const trailDays =
    nothingAfter && input.emptyTrailPx != null
      ? Math.min(
          TIMELINE_TRAIL_MARGIN_DAYS,
          Math.ceil(Math.max(0, input.emptyTrailPx) / pxPerDay)
        )
      : TIMELINE_TRAIL_MARGIN_DAYS;
  let linearStartDay = firstMilestoneDay - leadDays;
  let linearEndDay = lastMilestoneDay + trailDays;
  const padDays = Math.ceil(FULL_TIMELINE_EDGE_PAD_PX / pxPerDay);
  // Both ends fit endItemDays, so a filter leaves no empty dates on either side.
  if (input.fullTimeline && endItems.length) {
    linearStartDay = Math.min(linearStartDay, Math.min(...endItems) - padDays);
    linearEndDay = Math.max(linearEndDay, Math.max(...endItems) + padDays);
  }

  const before = input.fullTimeline
    ? []
    : items.filter((day) => day < linearStartDay);
  const after = input.fullTimeline
    ? []
    : items.filter((day) => day > linearEndDay);
  const leftW = before.length ? OVERFLOW_ZONE_WIDTH_PX : 0;
  const linearStartX = leftW;
  const linearEndX = linearStartX + (linearEndDay - linearStartDay) * pxPerDay;
  const left: OverflowZone | null = before.length
    ? {
        side: "left",
        start: 0,
        end: OVERFLOW_ZONE_WIDTH_PX,
        furthestDay: Math.min(...before)
      }
    : null;
  const right: OverflowZone | null = after.length
    ? {
        side: "right",
        start: linearEndX,
        end: linearEndX + OVERFLOW_ZONE_WIDTH_PX,
        furthestDay: Math.max(...after)
      }
    : null;
  // Usable width inside a zone: from its linear edge to INNER_PAD from its
  // outer edge.
  const zoneRun = OVERFLOW_ZONE_WIDTH_PX - OVERFLOW_ZONE_INNER_PAD_PX;

  const x = (day: number): number => {
    if (day < linearStartDay) {
      if (!left) return linearStartX - (linearStartDay - day) * pxPerDay;
      const daysIn = linearStartDay - left.furthestDay;
      const share = daysIn > 0 ? (linearStartDay - day) / daysIn : 1;
      return left.end - Math.min(1, share) * zoneRun;
    }
    if (day > linearEndDay) {
      if (!right) return linearEndX + (day - linearEndDay) * pxPerDay;
      const daysIn = right.furthestDay - linearEndDay;
      const share = daysIn > 0 ? (day - linearEndDay) / daysIn : 1;
      return right.start + Math.min(1, share) * zoneRun;
    }
    return linearStartX + (day - linearStartDay) * pxPerDay;
  };

  const at = (px: number): ScalePoint => {
    if (left && px < linearStartX) {
      return { kind: "before", day: linearStartDay };
    }
    if (right && px > linearEndX) return { kind: "after", day: linearEndDay };
    const day = Math.round(linearStartDay + (px - linearStartX) / pxPerDay);
    if (day < linearStartDay) return { kind: "date", day: linearStartDay };
    if (day > linearEndDay) return { kind: "date", day: linearEndDay };
    return { kind: "date", day };
  };

  const dayAt = (px: number): number => {
    if (left && px < linearStartX) {
      const share = Math.min(1, (left.end - px) / zoneRun);
      return linearStartDay - share * (linearStartDay - left.furthestDay);
    }
    if (right && px > linearEndX) {
      const share = Math.min(1, (px - right.start) / zoneRun);
      return linearEndDay + share * (right.furthestDay - linearEndDay);
    }
    return linearStartDay + (px - linearStartX) / pxPerDay;
  };

  const zoneOf = (day: number): "left" | "right" | null => {
    if (left && day < linearStartDay) return "left";
    if (right && day > linearEndDay) return "right";
    return null;
  };

  return {
    firstMilestoneDay,
    lastMilestoneDay,
    linearStartDay,
    linearEndDay,
    pxPerDay,
    linearStartX,
    linearEndX,
    left,
    right,
    width: right ? right.end : linearEndX,
    x,
    at,
    dayAt,
    zoneOf
  };
}

export type AxisTickUnit = "halfMonth" | "month" | "quarter" | "year";

/** Rough days per tick of each unit, finest first. */
const UNIT_DAYS: Array<[AxisTickUnit, number]> = [
  ["halfMonth", 15],
  ["month", 30],
  ["quarter", 91],
  ["year", 365]
];

/** Full-timeline axis ticks: the finest of 15 days / month / quarter / year
 *  whose spacing at pxPerDay is at least minGapPx, so a range of several
 *  years stays readable. 15-day ticks step from anchorDay (the first
 *  milestone, as the default axis does); the others sit on the 1st of a
 *  month, quarter or year. */
export function fullTimelineTicks(input: {
  startDay: number;
  endDay: number;
  pxPerDay: number;
  anchorDay: number;
  minGapPx: number;
}): { unit: AxisTickUnit; days: number[] } {
  const { startDay, endDay, pxPerDay, anchorDay, minGapPx } = input;
  const unit =
    UNIT_DAYS.find(([, days]) => days * pxPerDay >= minGapPx)?.[0] ?? "year";
  const days: number[] = [];
  if (unit === "halfMonth") {
    const from = Math.ceil((startDay - anchorDay) / 15);
    const to = Math.floor((endDay - anchorDay) / 15);
    for (let step = from; step <= to; step += 1) {
      days.push(anchorDay + step * 15);
    }
    return { unit, days };
  }
  const monthsPerTick = unit === "month" ? 1 : unit === "quarter" ? 3 : 12;
  const start = new Date(startDay * MS_PER_DAY);
  const year = start.getUTCFullYear();
  let month = Math.floor(start.getUTCMonth() / monthsPerTick) * monthsPerTick;
  for (;;) {
    // Date.UTC rolls month 12+ into the following years.
    const day = Date.UTC(year, month, 1) / MS_PER_DAY;
    if (day > endDay) break;
    if (day >= startDay) days.push(day);
    month += monthsPerTick;
  }
  return { unit, days };
}
